import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useLocation } from "react-router-dom";
import { fetchSubscriptionMe } from "@/src/services/subscriptionService";
import {
  clearHomeownerSubscriptionCache,
  getHomeownerSubscriptionSnapshot,
  setHomeownerSubscriptionSnapshot,
} from "@/src/lib/homeownerSubscriptionCache";
import type { Entitlements, SubscriptionDetails } from "@/src/types/subscriptionPlan.types";
import { notifySubscriptionError } from "@/src/utils/subscriptionErrors";
import { useAppSelector } from "@/src/redux/hooks";

type HomeownerSubscriptionContextValue = {
  subscription: SubscriptionDetails | null;
  entitlements: Entitlements | null;
  isLoading: boolean;
  error: string | null;
  refetch: (options?: { force?: boolean }) => Promise<void>;
  invalidate: () => void;
};

const HomeownerSubscriptionContext =
  createContext<HomeownerSubscriptionContextValue | null>(null);

function applyMeData(
  data: Awaited<ReturnType<typeof fetchSubscriptionMe>>,
  setSubscription: (v: SubscriptionDetails | null) => void,
  setEntitlements: (v: Entitlements | null) => void,
) {
  setSubscription(data.subscription);
  setEntitlements(data.entitlements);
}

export function HomeownerSubscriptionProvider({ children }: { children: ReactNode }) {
  const { pathname } = useLocation();
  const { isAuthenticated, accessToken, user } = useAppSelector((state) => state.auth);
  const userId = user?.id ?? null;

  const [subscription, setSubscription] = useState<SubscriptionDetails | null>(null);
  const [entitlements, setEntitlements] = useState<Entitlements | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchInFlight = useRef<Promise<void> | null>(null);
  const lastFetchedPathname = useRef<string | null>(null);

  const shouldLoad = Boolean(isAuthenticated && accessToken && userId);

  const invalidate = useCallback(() => {
    clearHomeownerSubscriptionCache();
    setSubscription(null);
    setEntitlements(null);
    lastFetchedPathname.current = null;
  }, []);

  const load = useCallback(
    async (options?: { force?: boolean }) => {
      if (!userId || !shouldLoad) return;

      const snapshot = getHomeownerSubscriptionSnapshot(userId);
      if (snapshot) {
        applyMeData(snapshot, setSubscription, setEntitlements);
        setIsLoading(false);
      } else {
        setIsLoading(true);
      }

      if (fetchInFlight.current && !options?.force) {
        await fetchInFlight.current;
        return;
      }

      setError(null);

      const task = (async () => {
        try {
          const data = await fetchSubscriptionMe();
          setHomeownerSubscriptionSnapshot(userId, data);
          applyMeData(data, setSubscription, setEntitlements);
        } catch (err) {
          if (!notifySubscriptionError(err)) {
            setError(err instanceof Error ? err.message : "Failed to load subscription");
          }
        } finally {
          setIsLoading(false);
          fetchInFlight.current = null;
        }
      })();

      fetchInFlight.current = task;
      await task;
    },
    [shouldLoad, userId],
  );

  const loadRef = useRef(load);
  loadRef.current = load;

  const refetch = useCallback(
    async (options?: { force?: boolean }) => {
      clearHomeownerSubscriptionCache();
      await load({ force: options?.force !== false });
    },
    [load],
  );

  useEffect(() => {
    if (isAuthenticated && accessToken && userId) return;
    invalidate();
    setIsLoading(false);
  }, [accessToken, invalidate, isAuthenticated, userId]);

  /** Fetch /subscriptions/me only when the homeowner route changes (not on clicks). */
  useEffect(() => {
    if (!isAuthenticated || !accessToken || !userId) {
      return;
    }

    if (!shouldLoad) {
      lastFetchedPathname.current = null;
      return;
    }

    if (lastFetchedPathname.current === pathname) {
      return;
    }

    lastFetchedPathname.current = pathname;
    void loadRef.current();
  }, [accessToken, isAuthenticated, pathname, shouldLoad, userId]);

  const value = useMemo(
    () => ({
      subscription,
      entitlements,
      isLoading,
      error,
      refetch,
      invalidate,
    }),
    [subscription, entitlements, isLoading, error, refetch, invalidate],
  );

  return (
    <HomeownerSubscriptionContext.Provider value={value}>
      {children}
    </HomeownerSubscriptionContext.Provider>
  );
}

export function useHomeownerSubscriptionContext(): HomeownerSubscriptionContextValue {
  const ctx = useContext(HomeownerSubscriptionContext);
  if (!ctx) {
    throw new Error(
      "useHomeownerSubscription must be used within HomeownerSubscriptionProvider",
    );
  }
  return ctx;
}
